/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { useTheme } from "styled-components";
import { format } from "date-fns";
import PluginCard from "../../ui/PluginCard";
import StartDatePicker from "../../ui/form/StartDatePicker";
import AddToCartControls from "../../AddToCartControls";
import Icon from "../../ui/Icon";
import { localDate } from "../../../utils/helpers/date";
import Button from "../../ui/Button";
import Flex from "../../ui/Flex";
import FormatDateTime from "../../formatters/FormatDateTime";
import FormatDuration from "../../formatters/FormatDuration";

export const normalizePackageInstance = (instance) => ({
  ...instance.package,
  id: instance.id,
  period_start:                instance.period_start,
  period_end:                  instance.period_end,
  instance_services:           instance.services,
  instance_products:           instance.products,
  instance_paid_attributes:    instance.paid_attributes,
  count_used_package_instance: instance.count_used_package_instance,
  is_used: instance.is_used,
});

const PackageItem = React.memo(({ pkg, isSelected, initialDate, onSelect, onDateChange, readOnly }) => {
  const [selectedDate, setSelectedDate] = useState(() => (initialDate ? localDate(initialDate) : new Date()));
  const theme = useTheme();

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (isSelected) onDateChange(pkg.id, format(date, 'yyyy-MM-dd'));
  };

  const toAvailableMap = (arr, idKey) =>
    (arr || []).reduce((acc, x) => { acc[x[idKey]] = x.qty; return acc; }, {});

  const availableServices   = toAvailableMap(pkg.instance_services,        "service_id");
  const availableProducts   = toAvailableMap(pkg.instance_products,        "product_id");
  const availablePaidAttrs  = toAvailableMap(pkg.instance_paid_attributes, "product_id");

  const withUsed = (item, idKey, availableMap) => readOnly && pkg.instance_services !== undefined
    ? { availableQty: availableMap[item[idKey]] ?? 0, totalQty: item.qty }
    : { qty: item.qty };

  const packageLimitItem = +pkg.is_use_package_limit && +pkg.package_limit ? [{
    icon: <Icon.FileList />,
    name: "General items limit",
    ...(readOnly && pkg.count_used_package_instance != null
      ? { availableQty: pkg.package_limit - pkg.count_used_package_instance, totalQty: pkg.package_limit }
      : { qty: pkg.package_limit }),
  }] : [];

  const includes = [
    ...packageLimitItem,
    ...(pkg.services       || []).map(item => ({ icon: <Icon.ServicePlaceholderIcon strokeWidth={3}/>, name: item.name, ...withUsed(item, "service_id", availableServices)   })),
    ...(pkg.products       || []).map(item => ({ icon: <Icon.Bag strokeWidth={5}/>, name: item.name, ...withUsed(item, "product_id", availableProducts)   })),
    ...(pkg.paid_attributes || []).map(item => ({ icon: <Icon.Gift />, name: item.name, ...withUsed(item, "product_id", availablePaidAttrs)  })),
  ];

  const stickyInclude = (pkg.period_start && pkg.period_end)
    ? { icon: <Icon.CalendarDetailed />, label: "Valid From – To", name: <><FormatDateTime value={pkg.period_start} format={FormatDateTime.FORMAT.DATE} /> – <FormatDateTime value={pkg.period_end} format={FormatDateTime.FORMAT.DATE} /></> }
    : pkg.duration
      ? { icon: <Icon.CalendarDetailed />, name: <FormatDuration value={pkg.duration} type={pkg.duration_type} /> }
      : null;

  const canBePurchased = !!+pkg.can_be_purchased;

  const controls = readOnly ? null : (
    !canBePurchased
      ? <Button
            title="Sold out"
            variant="outline"
            fullWidth
            disabled={true}
        />
      : <AddToCartControls
          inCart={isSelected}
          onAdd={() => onSelect(pkg, format(selectedDate, 'yyyy-MM-dd'))}
          onRemove={() => onSelect(pkg, format(selectedDate, 'yyyy-MM-dd'))}
        />
  );

  const datePicker = readOnly ? null : (
    <Flex style={{textWrapMode: 'nowrap'}}>
      <StartDatePicker
        date={selectedDate}
        onChange={handleDateSelect}
        minDate={new Date()}
      />
    </Flex>
  );

  const modalStickyInclude = (!readOnly && stickyInclude && pkg.duration)
    ? { ...stickyInclude, action: datePicker }
    : null;

  return (
    <PluginCard
      name={pkg.name}
      price={!readOnly && pkg.price}
      currency={pkg.currency}
      description={pkg.description}
      picture={pkg.picture_path}
      fallbackIcon={<Icon.BoxArchive size="90px" color="iconStroke" strokeWidth={1} />}
      isSelected={isSelected}
      includes={includes}
      includeLimit={3}
      stickyInclude={stickyInclude}
      beforeFooter={datePicker}
      footer={controls}
      modalFooter={canBePurchased && controls}
      modalStickyInclude={modalStickyInclude}
      badge={pkg.is_used ? "Used" : undefined}
    />
  );
});

export default PackageItem;